import _ from 'lodash';
import moment from 'moment';
import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';

// Material UI
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import Button from '@material-ui/core/Button';

//- Components
import EnhancedTableHead from '../../components/table/EnhancedTableHead';

//- Helpers
import { isFiltered } from '../../libs/tableUtilities';
import { getGlobalClasses } from '../../themes';

//- Containers
import AuthUserContainer from '../../containers/AuthUser';
import EquipmentAssignmentContainer from '../../containers/EquipmentAssignment';
import FormComponent from '../../components/FormComponent';
import EndEquipmentAssignmentDialog from '../../components/dialogs/Equipment/EndEquipmentAssignmentDialog';
import SaveEquipmentDialog from '../../components/dialogs/Equipment/SaveEquipmentDialog';

const groupColors = [
	{ r: 149, g: 117, b: 205 },
	{ r: 100, g: 181, b: 246 },
	{ r: 174, g: 213, b: 129 },
	{ r: 255, g: 183, b: 77 },
	{ r: 255, g: 138, b: 101 },
	{ r: 229, g: 115, b: 115 },
	{ r: 186, g: 104, b: 200 },
];
function rgba(rgb, a) {
	const { r, g, b } = rgb;
	return `rgba(${r},${g},${b},${a})`;
}

const statusNamesToClassName = {
	retired: {
		color: '#616161',
		fontWeight: 900,
	},
	retiredStillAssigned: {
		fontWeight: 900,
		color: '#FF500F',
	},
	active: {
		fontWeight: 900,
		color: '#4CAF50',
	},
};

const statusToDisplay = {
	retired: 'Retired',
	retiredStillAssigned: 'Retired Still Assigned',
	active: 'Active',
};

const styles = (theme) => {
	return {
		...statusNamesToClassName,
		...getGlobalClasses(theme),
		table: {
			width: '100%',
			tableLayout: 'auto',
		},
		showPast: {
			borderBottom: '2px solid #eee',
			marginBottom: 30,
			paddingTop: 5,
			paddingBottom: 15,
		},
		sortGroupCell: {
			border: `1px solid #bbb`,
			overflowX: 'hidden',
			height: 'auto',
			position: 'relative',
			padding: 10,
			//backgroundColor : '#D1C4E9',
			textTransform: 'uppercase',
			letterSpacing: 2,
			color: 'white',
			fontWeight: 500,
		},
		geoTabComment: {
			fontSize: '85%',
			color: '#666',
			paddingLeft: 5,
		},
		typeCount: {
			fontSize: '80%',
			paddingLeft: 10,
			letterSpacing: 1,
		},
	};
};

class EquipmentAssignmentTable extends FormComponent {
	constructor(props) {
		super(props);

		this.state = {
			order: 'asc',
			orderBy: 'parentChildDisplayName',
			filter: '',
			tabIndex: 0,
			filterMap: {},
			equipmentAssignmentToEnd: undefined,
			isEndDialogOpen: false,
		};
	}

	componentDidMount() {
		if (!this.props.equipmentAssignments.length) {
			this.props.searchEquipmentAssignments();
		}
	}

	// dialogs
	handleAdd = () => {
		this.setState({
			isSaveDialogOpen: true,
		});
	};
	handleUpdate = (equipmentAssignment) => {
		this.setState({
			equipmentAssignmentToUpdate: equipmentAssignment,
			isSaveDialogOpen: true,
		});
	};
	handleSaveDialogClosed = () => {
		this.setState({
			isSaveDialogOpen: false,
		});
	};

	handleEnd = (equipmentAssignment) => {
		this.setState({
			equipmentAssignmentToEnd: equipmentAssignment,
			isEndDialogOpen: true,
		});
	};

	handleEndDialogClosed = () => {
		this.setState({
			isEndDialogOpen: false,
		});
	};

	// table
	handleSort = (orderBy) => {
		let order = 'desc';
		if (this.state.orderBy === orderBy && this.state.order === 'desc') {
			order = 'asc';
		}
		this.setState({ order, orderBy });
	};
	handleFilter = (event) => {
		this.setState({ filter: event.target.value });
	};

	render() {
		const { equipmentAssignments, selectedLocationId } = this.props;

		if (!equipmentAssignments) {
			return <div>Fetching...</div>;
		}

		return (
			<Fragment>
				{!selectedLocationId && this.renderPleaseSelect()}
				{selectedLocationId && this.renderTable()}
			</Fragment>
		);
	}

	renderPleaseSelect = () => {
		return <div>Please select a location</div>;
	};

	renderTable = () => {
		const {
			classes,
			equipmentAssignments,
			equipmentMap,
			locationMap,
			includeEdit,
			selectedLocationId,
		} = this.props;

		const { order, orderBy, filter } = this.state;

		const countByLocation = {};
		let equipmentAssignmentRows = equipmentAssignments.reduce((result, equipmentAssignment) => {
			const { equipmentAssignmentId, equipmentId, locationId, dateCreated } = equipmentAssignment;

			if (selectedLocationId !== locationId && selectedLocationId !== 'all') {
				return result;
			}

			const dateCreatedFormatted = moment(dateCreated).format('MMM Do, YYYY');

			const location = locationMap[locationId] || { display: 'Missing' };
			const locationDisplay = location.display;

			const equipment = equipmentMap[equipmentId] || { display: 'Missing', status: 'missing' };
			const equipmentDisplay = (
				<>
					{equipment.display}{' '}
					<span className={classes.geoTabComment}>{equipment.geoTabComment}</span>
				</>
			);

			const row = {
				equipmentAssignmentId,
				equipmentId,
				truckNumber: equipment.display,
				equipmentDisplay,
				status: equipment.status,
				statusDisplay: statusToDisplay[equipment.status],
				locationId,
				locationDisplay,
				endDate: 'No End Date',
				dateCreated,
				dateCreatedFormatted,
				onUpdateClick: () => this.handleUpdate(equipmentAssignment),
				onEndClick: () => this.handleEnd(equipmentAssignment),
			};

			if (isFiltered({ filter, row, fields: ['equipmentDisplay', 'locationDisplay'] })) {
				return result;
			}

			if (!countByLocation[locationId]) {
				countByLocation[locationId] = 0;
			}
			countByLocation[locationId]++;

			result.push(row);
			return result;
		}, []);

		// equipmentAssignmentRows = sortTable({rows: equipmentAssignmentRows, order, orderBy});
		// equipmentAssignmentRows = sortTable({rows: equipmentAssignmentRows, order, orderBy});
		equipmentAssignmentRows = _.orderBy(equipmentAssignmentRows, [
			'locationDisplay',
			'truckNumber',
		]);

		const equipmentAssignmentColumns = [
			{
				id: 'truck',
				label: 'Truck',
				isNotSortable: true,
			},
			{
				id: 'location',
				label: 'Location',
				isNotSortable: true,
			},
			{
				id: 'endDate',
				label: 'End Date',
				isNotSortable: true,
			},
			{
				id: 'dateCreated',
				label: 'Date Created',
				isNotSortable: true,
			},
			{
				id: 'statusDisplay',
				label: 'GeoTab Status',
				isNotSortable: true,
			},
		];

		if (includeEdit) {
			equipmentAssignmentColumns.push({
				id: 'edit',
				label: 'Edit',
				isNotSortable: true,
			});
		}

		const { isEndDialogOpen, equipmentAssignmentToEnd } = this.state;
		const { isSaveDialogOpen, equipmentAssignmentToUpdate } = this.state;

		let lastLocationDisplay;
		let currentGroupIndex = 0;
		return (
			<>
				<Table size="small" className={classes.table}>
					<EnhancedTableHead
						columns={equipmentAssignmentColumns}
						order={order}
						orderBy={orderBy}
						onRequestSort={this.handleSort}
					/>
					<TableBody>
						{equipmentAssignmentRows.map((row) => {
							const { locationDisplay, locationId } = row;

							let style;
							let scheduleDisplay;
							let drawBreak = false;
							if (lastLocationDisplay !== locationDisplay) {
								drawBreak = true;
								lastLocationDisplay = locationDisplay;
								const color = groupColors[currentGroupIndex % groupColors.length];
								style = {
									background:
										`linear-gradient(to right, ${rgba(color, 1)} 0%,` +
										`${rgba(color, 0.75)} 20%,` +
										`${rgba(color, 0)} 98%)`,
								};
								currentGroupIndex++;

								scheduleDisplay = locationDisplay;
							}
							return (
								<Fragment key={row.equipmentAssignmentId}>
									{drawBreak && (
										<tr key={locationDisplay}>
											<td
												className={classes.sortGroupCell}
												style={style}
												colSpan={equipmentAssignmentColumns.length}
											>
												{scheduleDisplay}
												<span className={classes.typeCount}>
													Truck Count: {countByLocation[locationId]}
												</span>
											</td>
										</tr>
									)}

									<TableRow hover className={classes.row} key={row.equipmentAssignmentId}>
										<TableCell>{row.equipmentDisplay}</TableCell>
										<TableCell>{row.locationDisplay}</TableCell>
										<TableCell>
											<i style={{ color: '#999' }}>{row.endDate}</i>
										</TableCell>
										<TableCell>{row.dateCreatedFormatted}</TableCell>
										<TableCell className={classes[row.status]}>{row.statusDisplay}</TableCell>
										{includeEdit && (
											<TableCell style={{ whiteSpace: 'nowrap' }}>
												<Button
													variant="outlined"
													color="primary"
													onClick={row.onUpdateClick}
													style={{ marginRight: 5 }}
												>
													Reassign
												</Button>
												<Button variant="outlined" onClick={row.onEndClick}>
													End
												</Button>
											</TableCell>
										)}
									</TableRow>
								</Fragment>
							);
						})}
					</TableBody>
				</Table>

				<EndEquipmentAssignmentDialog
					open={isEndDialogOpen}
					onClose={this.handleEndDialogClosed}
					equipmentAssignmentToEnd={equipmentAssignmentToEnd}
					equipmentMap={equipmentMap}
				/>

				<SaveEquipmentDialog
					open={isSaveDialogOpen}
					onClose={this.handleSaveDialogClosed}
					equipmentAssignmentToUpdate={equipmentAssignmentToUpdate}
					equipmentMap={equipmentMap}
				/>
			</>
		);
	};
}

EquipmentAssignmentTable.propTypes = {
	classes: PropTypes.object.isRequired,

	searchEquipmentAssignments: PropTypes.func.isRequired,
	equipmentAssignments: PropTypes.array,
	equipmentMap: PropTypes.object,

	order: PropTypes.string,
	orderBy: PropTypes.string,
	history: PropTypes.object,
	theme: PropTypes.object.isRequired,
	includeEdit: PropTypes.bool,
	selectedLocationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	showPast: PropTypes.bool,
};

export default _.flow([
	withStyles(styles, { withTheme: true }),
	AuthUserContainer,
	EquipmentAssignmentContainer,
])(EquipmentAssignmentTable);
